import { IntegrationType } from 'libs/end-to-end/src/types/databases/integrations/integration-type';
import { OrganizationSecretItems } from 'libs/end-to-end/src/types/secrets/organization-secret-items';
import { SecretReference } from 'libs/end-to-end/src/types/secrets/secret-reference';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { ReadPatternHook } from 'apps/sigma2-ui/src/lib/hooks/useReadPattern';
import { DataSetEditHTTPIntegrationHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditHTTPIntegration';
import { DataSetEditMergeIntegrationHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditMergeIntegration';
import { DataSetEditPostgresIntegrationHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditPostgresIntegration';
import { DataSetIntegrationFieldValidationError } from 'libs/end-to-end/src/data-sets';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { DataSetSecretSelector } from 'apps/sigma2-ui/src/data-wizard/components/DataSetSecretSelector';
import React, { useCallback } from 'react';
import { SimpleInput } from 'apps/sigma2-ui/src/components/ui/SimpleInput';
import { safeParseInt } from 'libs/end-to-end/src/math';
import { DataSetHTTPConfigurationURLListComponent } from 'apps/sigma2-ui/src/data-wizard/components/DataSetHTTPConfigurationURLListComponent';
import { DataSetHTTPConfigurationCustomHeaderListComponent } from 'apps/sigma2-ui/src/data-wizard/components/DataSetHTTPConfigurationCustomHeaderListComponent';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { MergeCategoryEnums } from 'libs/end-to-end/src/merge';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { MergeAccountToken } from 'libs/end-to-end/src/merge';
import { getMergeCategoryEnumForString } from 'libs/end-to-end/src/merge';
import { useMergeLink } from '@mergeapi/react-merge-link';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { DataSetRubricMappingComponent } from 'apps/sigma2-ui/src/data-wizard/components/DataSetRubricMappingComponent';
import { ResumeQualityScoreRubric } from 'libs/end-to-end/src/types/resume-parser/resume-quality-score-rubric';
import { AI_INTERVIEWER_DEBUG_MODE_SELECT_ITEMS } from 'libs/end-to-end/src/ai-interviewer';
import { AI_INTERVIEWER_DEBUG_MODE_DEFAULT } from 'libs/end-to-end/src/types/ai-interviewer/ai-interviewer-debug-mode';
import { AIInterviewerDebugMode } from 'libs/end-to-end/src/types/ai-interviewer/ai-interviewer-debug-mode';
import { replaceTextColor } from 'apps/sigma2-ui/src/components/ui/Typography';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { DataSetMergePassthroughRequestsComponent } from 'apps/sigma2-ui/src/data-wizard/components/DataSetMergePassthroughRequestsComponent';
import { MergeIntegrationPassthroughRequest } from 'libs/end-to-end/src/types/databases/integrations/merge-integration';
import { QuestionRubric } from 'libs/end-to-end/src/types/ai-interviewer/ai-interviewer-rubric';
import { createQuestionRubric } from 'apps/sigma2-ui/src/data-wizard/components/DataSetRubricMappingComponent';
import { AllowedOpenAiModels } from 'libs/end-to-end/src/types/databases/data-sets/data-set-resume-rubric';
import { ResumeGradingOptions } from 'apps/sigma2-ui/src/data-wizard/components/ResumeGradingOptions';
import { DataSetPostgresConfigurationTableListComponent } from 'apps/sigma2-ui/src/data-wizard/components/DataSetPostgresConfigurationTableListComponent';
import { DataSetADPConfigurationComponent } from 'apps/sigma2-ui/src/data-wizard/components/DataSetADPConfigurationComponent';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { DataSetIntegrationSnowflakeConfigurationComponent } from 'apps/sigma2-ui/src/data-wizard/components/DataSetIntegrationSnowflakeConfigurationComponent';
import { DbtAction } from 'libs/end-to-end/src/types/databases/integrations/snowflake-integration';
import { DataSetEditSqlServerIntegrationHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditSqlServerIntegration';
import { DataSetSqlServerConfigurationTableListComponent } from 'apps/sigma2-ui/src/data-wizard/components/DataSetSqlServerConfigurationTableListComponent';
import { DataSetSqlServerConfigurationQueryListComponent } from 'apps/sigma2-ui/src/data-wizard/components/DataSetSqlServerConfigurationQueryListComponent';

export const DataSetIntegrationConfigurationComponent = ({
  integrationType,
  organizationId,
  clientIdSecretReference,
  clientSecretSecretReference,
  usernameSecretReference,
  passwordSecretReference,
  folderIdSecretReference,
  fileNameSecretReference,
  setClientIdSecretReference,
  setClientSecretSecretReference,
  setUsernameSecretReference,
  setPasswordSecretReference,
  setFolderIdSecretReference,
  setFileNameSecretReference,
  listSecretsPattern,
  apiKeySecretReference,
  setApiKeySecretReference,
  employeeHistoryUrlSecretReference,
  setCandidatesUrlSecretReference,
  setCompensationChangeHistoryUrlSecretReference,
  setEmployeeHistoryUrlSecretReference,
  setStaffingEventsUrlSecretReference,
  staffingEventsUrlSecretReference,
  compensationChangeHistoryUrlSecretReference,
  candidatesUrlSecretReference,
  setOauthUrlSecretReference,
  oauthUrlSecretReference,
  setApiBaseUrlSecretReference,
  apiBaseUrlSecretReference,
  sslCertSecretReference,
  setSslCertSecretReference,
  keyCertSecretReference,
  setKeyCertSecretReference,
  httpIntegrationHook,
  postgresIntegrationHook,
  sqlServerIntegrationHook,
  mergeIntegrationHook,
  tryCreateMergeMagicLink,
  integrationFieldValidationErrors,
  dbtAction,
  setDbtAction,
  agentMode,
  setAgentMode,
  agentDateCap,
  setAgentDateCap,
}: {
  organizationId: string;
  integrationType: IntegrationType;
  clientIdSecretReference: SecretReference | null;
  clientSecretSecretReference: SecretReference | null;
  folderIdSecretReference: SecretReference | null;
  fileNameSecretReference: SecretReference | null;
  setClientIdSecretReference: Setter<SecretReference | null>;
  setClientSecretSecretReference: Setter<SecretReference | null>;
  setUsernameSecretReference: Setter<SecretReference | null>;
  setFolderIdSecretReference: Setter<SecretReference | null>;
  setFileNameSecretReference: Setter<SecretReference | null>;
  listSecretsPattern: ReadPatternHook<OrganizationSecretItems>;
  apiKeySecretReference: SecretReference | null;
  setApiKeySecretReference: Setter<SecretReference | null>;
  usernameSecretReference: SecretReference | null;
  passwordSecretReference: SecretReference | null;
  setPasswordSecretReference: Setter<SecretReference | null>;
  employeeHistoryUrlSecretReference: SecretReference | null;
  setEmployeeHistoryUrlSecretReference: Setter<SecretReference | null>;
  staffingEventsUrlSecretReference: SecretReference | null;
  setStaffingEventsUrlSecretReference: Setter<SecretReference | null>;
  compensationChangeHistoryUrlSecretReference: SecretReference | null;
  setCompensationChangeHistoryUrlSecretReference: Setter<SecretReference | null>;
  candidatesUrlSecretReference: SecretReference | null;
  setCandidatesUrlSecretReference: Setter<SecretReference | null>;
  oauthUrlSecretReference: SecretReference | null;
  setOauthUrlSecretReference: Setter<SecretReference | null>;
  apiBaseUrlSecretReference: SecretReference | null;
  setApiBaseUrlSecretReference: Setter<SecretReference | null>;
  sslCertSecretReference: SecretReference | null;
  setSslCertSecretReference: Setter<SecretReference | null>;
  keyCertSecretReference: SecretReference | null;
  setKeyCertSecretReference: Setter<SecretReference | null>;
  httpIntegrationHook: DataSetEditHTTPIntegrationHook;
  postgresIntegrationHook: DataSetEditPostgresIntegrationHook;
  sqlServerIntegrationHook: DataSetEditSqlServerIntegrationHook;
  mergeIntegrationHook: DataSetEditMergeIntegrationHook;
  tryCreateMergeMagicLink: () => void;
  integrationFieldValidationErrors: DataSetIntegrationFieldValidationError[];
  dbtAction: DbtAction;
  setDbtAction: Setter<DbtAction>;
  agentMode: boolean | null;
  setAgentMode: Setter<boolean>;
  agentDateCap: string;
  setAgentDateCap: Setter<string>;
}) => {
  if (integrationType === 'Workday') {
    return (
      <DataSetWorkdayConfigurationComponent
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
        candidatesUrlSecretReference={candidatesUrlSecretReference}
        compensationChangeHistoryUrlSecretReference={
          compensationChangeHistoryUrlSecretReference
        }
        employeeHistoryUrlSecretReference={employeeHistoryUrlSecretReference}
        setCandidatesUrlSecretReference={setCandidatesUrlSecretReference}
        setCompensationChangeHistoryUrlSecretReference={
          setCompensationChangeHistoryUrlSecretReference
        }
        setEmployeeHistoryUrlSecretReference={
          setEmployeeHistoryUrlSecretReference
        }
        passwordSecretReference={passwordSecretReference}
        setPasswordSecretReference={setPasswordSecretReference}
        setStaffingEventsUrlSecretReference={
          setStaffingEventsUrlSecretReference
        }
        setUsernameSecretReference={setUsernameSecretReference}
        staffingEventsUrlSecretReference={staffingEventsUrlSecretReference}
        usernameSecretReference={usernameSecretReference}
      />
    );
  }

  if (integrationType === 'Greenhouse') {
    return (
      <DataSetGreenhouseConfigurationComponent
        organizationId={organizationId}
        apiKeySecretReference={apiKeySecretReference}
        setApiKeySecretReference={setApiKeySecretReference}
        listSecretsPattern={listSecretsPattern}
      />
    );
  }

  if (integrationType === 'ADP') {
    return (
      <DataSetADPConfigurationComponent
        organizationId={organizationId}
        clientIdSecretReference={clientIdSecretReference}
        clientSecretSecretReference={clientSecretSecretReference}
        setClientIdSecretReference={setClientIdSecretReference}
        setClientSecretSecretReference={setClientSecretSecretReference}
        setSslCertSecretReference={setSslCertSecretReference}
        sslCertSecretReference={sslCertSecretReference}
        keyCertSecretReference={keyCertSecretReference}
        setKeyCertSecretReference={setKeyCertSecretReference}
        listSecretsPattern={listSecretsPattern}
        agentMode={agentMode}
        setAgentMode={setAgentMode}
        agentDateCap={agentDateCap}
        setAgentDateCap={setAgentDateCap}
      />
    );
  }

  if (integrationType === 'ShareFile') {
    return (
      <DataSetShareFileConfigurationComponent
        organizationId={organizationId}
        clientIdSecretReference={clientIdSecretReference}
        clientSecretSecretReference={clientSecretSecretReference}
        fileNameSecretReference={fileNameSecretReference}
        folderIdSecretReference={folderIdSecretReference}
        passwordSecretReference={passwordSecretReference}
        usernameSecretReference={usernameSecretReference}
        setClientIdSecretReference={setClientIdSecretReference}
        setClientSecretSecretReference={setClientSecretSecretReference}
        setFileNameSecretReference={setFileNameSecretReference}
        setFolderIdSecretReference={setFolderIdSecretReference}
        setPasswordSecretReference={setPasswordSecretReference}
        setUsernameSecretReference={setUsernameSecretReference}
        oauthUrlSecretReference={oauthUrlSecretReference}
        setOauthUrlSecretReference={setOauthUrlSecretReference}
        apiBaseUrlSecretReference={apiBaseUrlSecretReference}
        setApiBaseUrlSecretReference={setApiBaseUrlSecretReference}
        listSecretsPattern={listSecretsPattern}
      />
    );
  }

  if (integrationType === 'HTTP') {
    return (
      <DataSetHTTPConfigurationComponent
        organizationId={organizationId}
        httpIntegrationHook={httpIntegrationHook}
        listSecretsPattern={listSecretsPattern}
      />
    );
  }

  if (integrationType === 'Postgres') {
    return (
      <DataSetPostgresConfigurationComponent
        organizationId={organizationId}
        postgresIntegrationHook={postgresIntegrationHook}
        listSecretsPattern={listSecretsPattern}
      />
    );
  }

  if (integrationType === 'SqlServer') {
    return (
      <DataSetSqlServerConfigurationComponent
        organizationId={organizationId}
        sqlServerIntegrationHook={sqlServerIntegrationHook}
        listSecretsPattern={listSecretsPattern}
      />
    );
  }

  if (integrationType === 'Merge') {
    return (
      <DataSetMergeConfigurationComponent
        mergeIntegrationHook={mergeIntegrationHook}
        tryCreateMergeMagicLink={tryCreateMergeMagicLink}
        integrationFieldValidationErrors={integrationFieldValidationErrors}
      />
    );
  }

  if (integrationType === 'Snowflake') {
    return (
      <DataSetIntegrationSnowflakeConfigurationComponent
        dbtAction={dbtAction}
        setDbtAction={setDbtAction}
      />
    );
  }

  if (integrationType === 'Peregrine') {
    return (
      <DataSetPeregrineConfigurationComponent
        organizationId={organizationId}
        usernameSecretReference={usernameSecretReference}
        passwordSecretReference={passwordSecretReference}
        setUsernameSecretReference={setUsernameSecretReference}
        setPasswordSecretReference={setPasswordSecretReference}
        listSecretsPattern={listSecretsPattern}
      />
    );
  }

  throw guardExhaustive(integrationType);
};

export const DataSetWorkdayConfigurationComponent = ({
  organizationId,
  listSecretsPattern,
  usernameSecretReference,
  setUsernameSecretReference,
  passwordSecretReference,
  setPasswordSecretReference,
  employeeHistoryUrlSecretReference,
  setEmployeeHistoryUrlSecretReference,
  staffingEventsUrlSecretReference,
  setStaffingEventsUrlSecretReference,
  compensationChangeHistoryUrlSecretReference,
  setCompensationChangeHistoryUrlSecretReference,
  candidatesUrlSecretReference,
  setCandidatesUrlSecretReference,
}: {
  organizationId: string;
  listSecretsPattern: ReadPatternHook<OrganizationSecretItems>;
  usernameSecretReference: SecretReference | null;
  setUsernameSecretReference: Setter<SecretReference | null>;
  passwordSecretReference: SecretReference | null;
  setPasswordSecretReference: Setter<SecretReference | null>;
  employeeHistoryUrlSecretReference: SecretReference | null;
  setEmployeeHistoryUrlSecretReference: Setter<SecretReference | null>;
  staffingEventsUrlSecretReference: SecretReference | null;
  setStaffingEventsUrlSecretReference: Setter<SecretReference | null>;
  compensationChangeHistoryUrlSecretReference: SecretReference | null;
  setCompensationChangeHistoryUrlSecretReference: Setter<SecretReference | null>;
  candidatesUrlSecretReference: SecretReference | null;
  setCandidatesUrlSecretReference: Setter<SecretReference | null>;
}) => {
  return (
    <Spacing size={'md'} direction={'vertical'}>
      <DataSetSecretSelector
        label={'Username'}
        secretReference={usernameSecretReference}
        setSecretReference={setUsernameSecretReference}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />

      <DataSetSecretSelector
        label={'Password'}
        secretReference={passwordSecretReference}
        setSecretReference={setPasswordSecretReference}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />

      <DataSetSecretSelector
        label={'Employee History URL'}
        secretReference={employeeHistoryUrlSecretReference}
        setSecretReference={setEmployeeHistoryUrlSecretReference}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />

      <DataSetSecretSelector
        label={'Staffing Events URL'}
        secretReference={staffingEventsUrlSecretReference}
        setSecretReference={setStaffingEventsUrlSecretReference}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />

      <DataSetSecretSelector
        label={'Compensation Change History URL'}
        secretReference={compensationChangeHistoryUrlSecretReference}
        setSecretReference={setCompensationChangeHistoryUrlSecretReference}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />

      <DataSetSecretSelector
        label={'Candidates URL'}
        secretReference={candidatesUrlSecretReference}
        setSecretReference={setCandidatesUrlSecretReference}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />
    </Spacing>
  );
};

export const DataSetGreenhouseConfigurationComponent = ({
  organizationId,
  apiKeySecretReference,
  setApiKeySecretReference,
  listSecretsPattern,
}: {
  organizationId: string;
  apiKeySecretReference: SecretReference | null;
  setApiKeySecretReference: Setter<SecretReference | null>;
  listSecretsPattern: ReadPatternHook<OrganizationSecretItems>;
}) => {
  return (
    <Spacing size={'md'} direction={'vertical'}>
      <DataSetSecretSelector
        label={'API Key'}
        secretReference={apiKeySecretReference}
        setSecretReference={setApiKeySecretReference}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />
    </Spacing>
  );
};

export const DataSetShareFileConfigurationComponent = ({
  clientIdSecretReference,
  setClientSecretSecretReference,
  folderIdSecretReference,
  passwordSecretReference,
  fileNameSecretReference,
  clientSecretSecretReference,
  setClientIdSecretReference,
  setFileNameSecretReference,
  setUsernameSecretReference,
  setPasswordSecretReference,
  setFolderIdSecretReference,
  usernameSecretReference,
  setOauthUrlSecretReference,
  oauthUrlSecretReference,
  apiBaseUrlSecretReference,
  setApiBaseUrlSecretReference,
  listSecretsPattern,
  organizationId,
}: {
  clientIdSecretReference: SecretReference | null;
  clientSecretSecretReference: SecretReference | null;
  usernameSecretReference: SecretReference | null;
  passwordSecretReference: SecretReference | null;
  folderIdSecretReference: SecretReference | null;
  fileNameSecretReference: SecretReference | null;
  setClientIdSecretReference: Setter<SecretReference | null>;
  setClientSecretSecretReference: Setter<SecretReference | null>;
  setUsernameSecretReference: Setter<SecretReference | null>;
  setPasswordSecretReference: Setter<SecretReference | null>;
  setFolderIdSecretReference: Setter<SecretReference | null>;
  setFileNameSecretReference: Setter<SecretReference | null>;
  oauthUrlSecretReference: SecretReference | null;
  setOauthUrlSecretReference: Setter<SecretReference | null>;
  apiBaseUrlSecretReference: SecretReference | null;
  setApiBaseUrlSecretReference: Setter<SecretReference | null>;
  listSecretsPattern: ReadPatternHook<OrganizationSecretItems>;
  organizationId: string;
}) => {
  return (
    <Spacing size={'md'} direction={'vertical'}>
      <DataSetSecretSelector
        label={'OAuth URL'}
        secretReference={oauthUrlSecretReference}
        setSecretReference={setOauthUrlSecretReference}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />

      <DataSetSecretSelector
        label={'API Base URL'}
        secretReference={apiBaseUrlSecretReference}
        setSecretReference={setApiBaseUrlSecretReference}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />

      <DataSetSecretSelector
        label={'Client ID'}
        secretReference={clientIdSecretReference}
        setSecretReference={setClientIdSecretReference}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />

      <DataSetSecretSelector
        label={'Client Secret'}
        secretReference={clientSecretSecretReference}
        setSecretReference={setClientSecretSecretReference}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />

      <DataSetSecretSelector
        label={'Username'}
        secretReference={usernameSecretReference}
        setSecretReference={setUsernameSecretReference}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />

      <DataSetSecretSelector
        label={'Password'}
        secretReference={passwordSecretReference}
        setSecretReference={setPasswordSecretReference}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />

      <DataSetSecretSelector
        label={'Folder ID'}
        secretReference={folderIdSecretReference}
        setSecretReference={setFolderIdSecretReference}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />

      <DataSetSecretSelector
        label={'File Name'}
        secretReference={fileNameSecretReference}
        setSecretReference={setFileNameSecretReference}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />
    </Spacing>
  );
};

export const DataSetHTTPConfigurationComponent = ({
  organizationId,
  httpIntegrationHook,
  listSecretsPattern,
}: {
  organizationId: string;
  httpIntegrationHook: DataSetEditHTTPIntegrationHook;
  listSecretsPattern: ReadPatternHook<OrganizationSecretItems>;
}) => {
  return (
    <Spacing size={'md'} direction={'vertical'}>
      <DataSetHTTPConfigurationURLListComponent
        httpIntegrationHook={httpIntegrationHook}
      />
      <DataSetHTTPConfigurationCustomHeaderListComponent
        httpIntegrationHook={httpIntegrationHook}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />
      <SimpleInput
        label={'Retry Count'}
        value={`${httpIntegrationHook.httpIntegration.retryCount}`}
        onChange={(value: string) => {
          const newValue = safeParseInt(value, 10);

          if (newValue !== null) {
            httpIntegrationHook.setHttpIntegration({
              ...httpIntegrationHook.httpIntegration,
              retryCount: newValue,
            });
          }
        }}
      />

      <SimpleInput
        label={'Timeout In Seconds'}
        value={`${httpIntegrationHook.httpIntegration.timeoutInSeconds}`}
        onChange={(value: string) => {
          const newValue = safeParseInt(value, 10);

          if (newValue !== null) {
            httpIntegrationHook.setHttpIntegration({
              ...httpIntegrationHook.httpIntegration,
              timeoutInSeconds: newValue,
            });
          }
        }}
      />
    </Spacing>
  );
};

export const DataSetMergeConfigurationComponent = ({
  mergeIntegrationHook,
  tryCreateMergeMagicLink,
  integrationFieldValidationErrors,
}: {
  mergeIntegrationHook: DataSetEditMergeIntegrationHook;
  tryCreateMergeMagicLink: () => void;
  integrationFieldValidationErrors: DataSetIntegrationFieldValidationError[];
}) => {
  const language = useLanguage();

  const { userAuthenticatedApiCall } = useUser();
  const { mergeIntegration } = mergeIntegrationHook;

  const onSuccess = useCallback(
    async (public_token: string) => {
      // Save the public_token as the mergeAccountToken of the integration
      const saveMergeAccountTokenResult = await userAuthenticatedApiCall({
        responseSchema: MergeAccountToken,
        url: `${getApiBaseUrl()}/merge/account-token`,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify({
            category: mergeIntegration.category,
            publicToken: public_token,
          }),
        },
      });

      if (
        saveMergeAccountTokenResult === null ||
        saveMergeAccountTokenResult.resultType === 'err'
      ) {
        return;
      }

      const data = saveMergeAccountTokenResult.value;

      mergeIntegrationHook.setMergeIntegration({
        ...mergeIntegration,
        accountToken: data.accountToken,
      });
    },
    [mergeIntegration, mergeIntegrationHook, userAuthenticatedApiCall],
  );

  const mergeCategoryOptions = MergeCategoryEnums.map((value) => {
    return { optionLabel: value, optionValue: value };
  });

  const { open, isReady } = useMergeLink({
    linkToken: mergeIntegration.linkToken || '',
    onSuccess,
  });

  const isAccountTokenSet =
    mergeIntegration.accountToken !== null &&
    mergeIntegration.accountToken !== '';
  const isLinkTokenSet =
    mergeIntegration.linkToken !== null && mergeIntegration.linkToken !== '';
  const isMagicLinkUrlSet =
    mergeIntegration.magicLinkUrl !== null &&
    mergeIntegration.magicLinkUrl !== '';
  const isCategorySet = mergeIntegration.category !== null;

  return (
    <Spacing size={'md'} direction={'vertical'}>
      <SelectBoxWithSearch
        type="single"
        list={mergeCategoryOptions}
        disabled={mergeIntegration.accountToken !== null}
        label={'Integration Category'}
        selected={mergeIntegration.category}
        setSelected={(value: string) => {
          if (value !== null) {
            mergeIntegrationHook.setMergeIntegration({
              ...mergeIntegration,
              category: getMergeCategoryEnumForString(value),
            });
          }
        }}
      />
      <div>
        <SimpleInput
          label={'Account Token'}
          value={`${mergeIntegration.accountToken}`}
          isPassword={true}
          onChange={(value: string) => {
            if (value !== null) {
              mergeIntegrationHook.setMergeIntegration({
                ...mergeIntegration,
                accountToken: value,
              });
            }
          }}
        />
      </div>

      <Spacing size={'sm'} direction={'horizontal'}>
        <Button
          behavior={{
            type: 'onClick',
            onClick: () => {
              if (!isReady) {
                return;
              }

              open();
            },
          }}
          text={'Link Account'}
          disabled={!isLinkTokenSet || isAccountTokenSet}
        />
        <Button
          behavior={{
            type: 'onClick',
            onClick: () => {
              window.open(mergeIntegration.magicLinkUrl || '', '_blank');
            },
          }}
          text={'Open Magic Link'}
          disabled={!isMagicLinkUrlSet || isAccountTokenSet}
        />
        <Button
          behavior={{
            type: 'onClick',
            onClick: () => {
              tryCreateMergeMagicLink();
            },
          }}
          text={'Generate Magic Link'}
          disabled={!isCategorySet || isAccountTokenSet}
        />
      </Spacing>

      {/* Incremental Data Sync */}
      <S2Checkbox
        checked={mergeIntegration.isIncrementalSyncEnabled}
        onChange={() => {
          mergeIntegrationHook.setMergeIntegration({
            ...mergeIntegration,
            isIncrementalSyncEnabled:
              !mergeIntegration.isIncrementalSyncEnabled,
          });
        }}
      >
        Incremental Data Sync Enabled
      </S2Checkbox>

      {/* Resume Parsing */}
      {/* Disable resume grading but keep the grading rubric when resume parsing is turned off */}
      <S2Checkbox
        checked={mergeIntegration.isResumeParsingEnabled ?? false}
        onChange={() => {
          mergeIntegrationHook.setMergeIntegration({
            ...mergeIntegration,
            isResumeParsingEnabled: !mergeIntegration.isResumeParsingEnabled,
            ...(mergeIntegration.isResumeParsingEnabled && {
              resumeQualityScoreEnabled: false,
            }),
          });
        }}
      >
        Resume Parsing Enabled
      </S2Checkbox>

      {/* Convert JSON to Parquet */}
      <S2Checkbox
        checked={mergeIntegration.isConvertToParquetEnabled}
        onChange={() => {
          mergeIntegrationHook.setMergeIntegration({
            ...mergeIntegration,
            isConvertToParquetEnabled:
              !mergeIntegration.isConvertToParquetEnabled,
          });
        }}
      >
        Output JSON to Parquet Files
      </S2Checkbox>

      {mergeIntegration.isResumeParsingEnabled && (
        <S2Checkbox
          checked={mergeIntegration.isResumeParsingFilteringDisabled}
          onChange={() => {
            mergeIntegrationHook.setMergeIntegration({
              ...mergeIntegration,
              isResumeParsingFilteringDisabled:
                !mergeIntegration.isResumeParsingFilteringDisabled,
            });
          }}
        >
          Disable Resume Parsing Filtering (All Resumes Will Be Parsed)
        </S2Checkbox>
      )}

      {/* If resume parsing is enabled, then resume grading can be enabled */}
      {mergeIntegration.isResumeParsingEnabled && (
        <>
          <S2Checkbox
            checked={mergeIntegration.resumeQualityScoreEnabled ?? false}
            onChange={() => {
              mergeIntegrationHook.setMergeIntegration({
                ...mergeIntegration,
                resumeQualityScoreEnabled:
                  !mergeIntegration.resumeQualityScoreEnabled,
              });
            }}
          >
            Resume Grading Enabled
          </S2Checkbox>
        </>
      )}

      {mergeIntegration.isResumeParsingEnabled && (
        <>
          <S2Checkbox
            checked={mergeIntegration.isResumeExtraFeaturesEnabled}
            onChange={() => {
              mergeIntegrationHook.setMergeIntegration({
                ...mergeIntegration,
                isResumeExtraFeaturesEnabled:
                  !mergeIntegration.isResumeExtraFeaturesEnabled,
              });
            }}
          >
            Resume Extra Features Enabled
          </S2Checkbox>
        </>
      )}

      {
        <>
          <S2Checkbox
            checked={mergeIntegration.snowflakeIntegrationEnabled ?? false}
            onChange={() => {
              mergeIntegrationHook.setMergeIntegration({
                ...mergeIntegration,
                snowflakeIntegrationEnabled:
                  !mergeIntegration.snowflakeIntegrationEnabled,
              });
            }}
          >
            Enable Snowflake Integration
          </S2Checkbox>
        </>
      }

      <SimpleInput
        label={'Resume Parsing Cache Buster'}
        value={mergeIntegration.resumeParsingCacheBusterVersion}
        onChange={(value: string) => {
          if (value !== null) {
            mergeIntegrationHook.setMergeIntegration({
              ...mergeIntegration,
              resumeParsingCacheBusterVersion: value,
            });
          }
        }}
      />

      {mergeIntegration.resumeQualityScoreEnabled && (
        <>
          <ResumeGradingOptions
            isResumeGradingEnabled={mergeIntegration.resumeQualityScoreEnabled}
            setIsResumeGradingEnabled={(value: boolean) => {
              mergeIntegrationHook.setMergeIntegration({
                ...mergeIntegration,
                resumeQualityScoreEnabled: value,
              });
            }}
            openAiModel={mergeIntegration.openAiModel}
            setOpenAiModel={(value: AllowedOpenAiModels) => {
              mergeIntegrationHook.setMergeIntegration({
                ...mergeIntegration,
                openAiModel: value,
              });
            }}
            resumeGradingRubric={
              mergeIntegration.resumeQualityScoreRubrics || []
            }
            setResumeGradingRubric={(value: ResumeQualityScoreRubric[]) => {
              mergeIntegrationHook.setMergeIntegration({
                ...mergeIntegration,
                resumeQualityScoreRubrics: value,
              });
            }}
          />
        </>
      )}

      {/* AI Interviewer Enabled */}
      <S2Checkbox
        checked={mergeIntegration.aiInterviewerEnabled ?? false}
        onChange={() => {
          mergeIntegrationHook.setMergeIntegration({
            ...mergeIntegration,
            aiInterviewerEnabled: !mergeIntegration.aiInterviewerEnabled,
          });
        }}
      >
        AI Interviewer Enabled
      </S2Checkbox>

      {mergeIntegration.aiInterviewerEnabled && (
        <>
          <SelectBoxWithSearch
            type="single"
            list={AI_INTERVIEWER_DEBUG_MODE_SELECT_ITEMS}
            label={'AI Interviewer Debug Mode'}
            selected={
              mergeIntegration.aiInterviewerDebugMode ??
              AI_INTERVIEWER_DEBUG_MODE_DEFAULT
            }
            setSelected={(value: string) => {
              if (value !== null) {
                mergeIntegrationHook.setMergeIntegration({
                  ...mergeIntegration,
                  aiInterviewerDebugMode: AIInterviewerDebugMode.parse(value),
                });
              }
            }}
          />

          <Spacing size="none" direction="vertical">
            <S2Typography
              variant={'headline-7'}
              translatedContent={language.aiInterviewerStartDate()}
            />
            {integrationFieldValidationErrors.includes(
              'invalid-ai-interviewer-start-date',
            ) && (
              <S2Typography
                variant={'body-7'}
                translatedContent={language.invalidAIInterviewerStartDateFormat()}
                dangerouslyOverrideClassNameFn={replaceTextColor(
                  'text-red-700',
                )}
              />
            )}
            <input
              className="form-input"
              type="text"
              value={mergeIntegration.aiInterviewerStartDate ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value !== null) {
                  mergeIntegrationHook.setMergeIntegration({
                    ...mergeIntegration,
                    aiInterviewerStartDate: value,
                  });
                }
              }}
              placeholder={'YYYY-MM-DD'}
            />
          </Spacing>
        </>
      )}

      {/* Question Rubric Mapping */}
      <DataSetRubricMappingComponent
        mapping={mergeIntegration.questionRubrics || []}
        setMapping={(value: QuestionRubric[]) => {
          mergeIntegrationHook.setMergeIntegration({
            ...mergeIntegration,
            questionRubrics: value,
          });
        }}
        titleComponent={
          <S2Typography
            translatedContent={language.dataSetQuestionRubricMappingTitle()}
            variant={'headline-5'}
          />
        }
        descriptionComponent={
          <S2Typography
            translatedContent={language.dataSetQuestionRubricMappingDescription()}
            variant={'body-6'}
          />
        }
        onAddItem={createQuestionRubric}
        itemDisplayField="question"
      />

      <DataSetMergePassthroughRequestsComponent
        passthroughRequests={mergeIntegration.passthroughRequests || []}
        setPassthroughRequests={(
          requests: MergeIntegrationPassthroughRequest[],
        ) => {
          mergeIntegrationHook.setMergeIntegration({
            ...mergeIntegration,
            passthroughRequests: requests,
          });
        }}
      />
    </Spacing>
  );
};

export const DataSetPostgresConfigurationComponent = ({
  organizationId,
  postgresIntegrationHook,
  listSecretsPattern,
}: {
  organizationId: string;
  postgresIntegrationHook: DataSetEditPostgresIntegrationHook;
  listSecretsPattern: ReadPatternHook<OrganizationSecretItems>;
}) => {
  return (
    <Spacing size={'md'} direction={'vertical'}>
      <SimpleInput
        label={'Postgres Host'}
        value={`${postgresIntegrationHook.postgresIntegration.host}`}
        onChange={(value: string) => {
          postgresIntegrationHook.setPostgresIntegration({
            ...postgresIntegrationHook.postgresIntegration,
            host: value,
          });
        }}
      />

      <SimpleInput
        label={'Postgres Port'}
        value={`${postgresIntegrationHook.postgresIntegration.port}`}
        onChange={(value: string) => {
          const newValue = safeParseInt(value, 10);

          if (newValue !== null) {
            postgresIntegrationHook.setPostgresIntegration({
              ...postgresIntegrationHook.postgresIntegration,
              port: newValue,
            });
          }
        }}
      />

      <SimpleInput
        label={'Postgres Username'}
        value={`${postgresIntegrationHook.postgresIntegration.user}`}
        onChange={(value: string) => {
          postgresIntegrationHook.setPostgresIntegration({
            ...postgresIntegrationHook.postgresIntegration,
            user: value,
          });
        }}
      />

      <DataSetSecretSelector
        label={'Postgres Password'}
        secretReference={
          postgresIntegrationHook.postgresIntegration.passwordReference ?? null
        }
        setSecretReference={(value) => {
          postgresIntegrationHook.setPostgresIntegration({
            ...postgresIntegrationHook.postgresIntegration,
            passwordReference: value ?? undefined,
          });
        }}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />

      <SimpleInput
        label={'Postgres Database Name'}
        value={`${postgresIntegrationHook.postgresIntegration.database}`}
        onChange={(value: string) => {
          postgresIntegrationHook.setPostgresIntegration({
            ...postgresIntegrationHook.postgresIntegration,
            database: value,
          });
        }}
      />

      <S2Checkbox
        onChange={() => {
          postgresIntegrationHook.setPostgresIntegration({
            ...postgresIntegrationHook.postgresIntegration,
            ssl: !postgresIntegrationHook.postgresIntegration.ssl,
          });
        }}
        checked={postgresIntegrationHook.postgresIntegration.ssl}
      >
        Connect with strict SSL
      </S2Checkbox>

      <SimpleInput
        label={'Retry Count'}
        value={`${postgresIntegrationHook.postgresIntegration.retryCount}`}
        onChange={(value: string) => {
          const newValue = safeParseInt(value, 10);

          if (newValue !== null) {
            postgresIntegrationHook.setPostgresIntegration({
              ...postgresIntegrationHook.postgresIntegration,
              retryCount: newValue,
            });
          }
        }}
      />

      <DataSetPostgresConfigurationTableListComponent
        postgresIntegrationHook={postgresIntegrationHook}
      />
    </Spacing>
  );
};

export const DataSetPeregrineConfigurationComponent = ({
  organizationId,
  usernameSecretReference,
  passwordSecretReference,
  setUsernameSecretReference,
  setPasswordSecretReference,
  listSecretsPattern,
}: {
  organizationId: string;
  usernameSecretReference: SecretReference | null;
  passwordSecretReference: SecretReference | null;
  setUsernameSecretReference: Setter<SecretReference | null>;
  setPasswordSecretReference: Setter<SecretReference | null>;
  listSecretsPattern: ReadPatternHook<OrganizationSecretItems>;
}) => {
  return (
    <Spacing size={'md'} direction={'vertical'}>
      <DataSetSecretSelector
        label={'Username'}
        secretReference={usernameSecretReference}
        setSecretReference={setUsernameSecretReference}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />

      <DataSetSecretSelector
        label={'Password'}
        secretReference={passwordSecretReference}
        setSecretReference={setPasswordSecretReference}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />
    </Spacing>
  );
};

export const DataSetSqlServerConfigurationComponent = ({
  organizationId,
  sqlServerIntegrationHook,
  listSecretsPattern,
}: {
  organizationId: string;
  sqlServerIntegrationHook: DataSetEditSqlServerIntegrationHook;
  listSecretsPattern: ReadPatternHook<OrganizationSecretItems>;
}) => {
  return (
    <Spacing size={'md'} direction={'vertical'}>
      <SimpleInput
        label={'SQL Server Host'}
        value={`${sqlServerIntegrationHook.sqlServerIntegration.host}`}
        onChange={(value: string) => {
          sqlServerIntegrationHook.setSqlServerIntegration({
            ...sqlServerIntegrationHook.sqlServerIntegration,
            host: value,
          });
        }}
      />

      <SimpleInput
        label={'SQL Server Port'}
        value={`${sqlServerIntegrationHook.sqlServerIntegration.port}`}
        onChange={(value: string) => {
          const newValue = safeParseInt(value, 10);

          if (newValue !== null) {
            sqlServerIntegrationHook.setSqlServerIntegration({
              ...sqlServerIntegrationHook.sqlServerIntegration,
              port: newValue,
            });
          }
        }}
      />

      <DataSetSecretSelector
        label={'SQL Server Username'}
        secretReference={
          sqlServerIntegrationHook.sqlServerIntegration.user ?? null
        }
        setSecretReference={(value) => {
          sqlServerIntegrationHook.setSqlServerIntegration({
            ...sqlServerIntegrationHook.sqlServerIntegration,
            user: value ?? undefined,
          });
        }}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />

      <DataSetSecretSelector
        label={'SQL Server Password'}
        secretReference={
          sqlServerIntegrationHook.sqlServerIntegration.password ?? null
        }
        setSecretReference={(value) => {
          sqlServerIntegrationHook.setSqlServerIntegration({
            ...sqlServerIntegrationHook.sqlServerIntegration,
            password: value ?? undefined,
          });
        }}
        organizationId={organizationId}
        listSecretsPattern={listSecretsPattern}
      />

      <SimpleInput
        label={'Request Timeout (seconds)'}
        value={
          sqlServerIntegrationHook.sqlServerIntegration.requestTimeoutInSeconds
            ? `${sqlServerIntegrationHook.sqlServerIntegration.requestTimeoutInSeconds}`
            : ''
        }
        onChange={(value: string) => {
          if (value.trim() === '') {
            // If blank, set to undefined (will default to 60 seconds)
            sqlServerIntegrationHook.setSqlServerIntegration({
              ...sqlServerIntegrationHook.sqlServerIntegration,
              requestTimeoutInSeconds: undefined,
            });
          } else {
            const newValue = safeParseInt(value, 10);
            if (newValue !== null && newValue > 0) {
              sqlServerIntegrationHook.setSqlServerIntegration({
                ...sqlServerIntegrationHook.sqlServerIntegration,
                requestTimeoutInSeconds: newValue,
              });
            }
          }
        }}
      />

      <DataSetSqlServerConfigurationTableListComponent
        sqlServerIntegrationHook={sqlServerIntegrationHook}
      />

      <DataSetSqlServerConfigurationQueryListComponent
        sqlServerIntegrationHook={sqlServerIntegrationHook}
      />
    </Spacing>
  );
};
